import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Search from './Search';
import Detail from './Detail';
import LibraryListing from './LibraryListing';
import { Box, CircularProgress } from '@mui/material';
import config from '../config';
import { Helmet } from 'react-helmet-async';
import { fetchSupplements } from '../Utils';
import { HealingIcon } from '../icons';

const encodePathSegment = (value) => encodeURIComponent(String(value || '').trim());

function Supplements() {
  const { supplementKey } = useParams();
  const navigate = useNavigate();
  const [searchFocused, setSearchFocused] = useState(false);
  const [supplements, setSupplements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setSearchFocused(!!supplementKey);
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLoadingComplete = () => {
    setSearchFocused(false);
  };

  const fetchData = async () => {
    try {
      const supplementsList = await fetchSupplements(config);
      const sortedSupplements = [...(supplementsList || [])].sort((a, b) => a.name.localeCompare(b.name));
      setSupplements(sortedSupplements);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  const groupedSupplements = React.useMemo(() => (
    supplements.reduce((groups, supplement) => {
      const firstLetter = supplement.name?.[0]?.toUpperCase() || '#';
      const groupKey = /[A-Z]/.test(firstLetter) ? firstLetter : '#';
      if (!groups[groupKey]) groups[groupKey] = [];
      groups[groupKey].push(supplement);
      return groups;
    }, {})
  ), [supplements]);

  const availableLetters = React.useMemo(() => Object.keys(groupedSupplements).sort(), [groupedSupplements]);

  const collectionPageSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "Supplements Database | myStack",
    "description": "Explore our database of supplements. Find information on benefits, usage, and scientific research.",
    "url": "https://my-stack.ai/supplements",
    "mainEntity": {
      "@type": "ItemList",
      "itemListElement": supplements.map((supplement, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "url": `https://my-stack.ai/supplements/${encodePathSegment(supplement.key)}`,
        "name": supplement.name
      }))
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: supplementKey ? 'auto' : '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        pt: supplementKey ? 3 : { xs: 2, sm: 3.5 },
        backgroundColor: 'background.default'
      }}
    >
      <Helmet>
        <title>Supplements Database & Research | myStack</title>
        <meta name="description" content="Explore detailed information and scientific research on various health supplements. Find supplements relevant to your health goals on myStack." />
        <link rel="canonical" href="https://my-stack.ai/supplements" />
        <script type="application/ld+json">
          {JSON.stringify(collectionPageSchema)}
        </script>
      </Helmet>

      {!supplementKey && (
        <LibraryListing
          itemKey={supplementKey}
          groupedItems={groupedSupplements}
          availableLetters={availableLetters}
          routeBase="supplements"
          searchLabel="Search supplements"
          heroTitle="Supplements Library"
          heroDescription="200+ supplements — what the research shows, dosages, and third-party tested products. All in one place."
          HeroIcon={HealingIcon}
          idPrefix="supplement"
          analyticsClickEvent="library_supplement_clicked"
          analyticsLetterEvent="library_az_letter_clicked"
        />
      )}

      {supplementKey && (
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Search
            itemKey={supplementKey}
            dataKey="supplements"
            detailKey="card"
            routeBase="supplements"
            label="Search supplements"
          />
        </Box>
      )}

      {supplementKey && (
        <Box sx={{ width: '100%', mt: 1 }}>
          <Detail
            dataKey="supplements"
            detailKey="card"
            onLoadingComplete={handleLoadingComplete}
          />
        </Box>
      )}
    </Box>
  );
}

export default Supplements;
